import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartConfig }                         from '../chart';
import { ChartHelper }                         from '../chart-helper';

interface ScatterPoint {
	cx: number;
	cy: number;
	r: number;
	fill: string;
	label: string;
	/** Tooltip body: axis-labelled x/y (and size when it's a bubble chart). */
	text: string;
}

/**
 * Scatter / bubble chart: every label is one point positioned on TWO numeric axes —
 * datasets[0] is x, datasets[1] is y, and an optional datasets[2] drives bubble AREA
 * (sqrt-scaled radius, so a 4× value reads as a 4× area, not a 16× one). Both axes get
 * nice snapped steps and support negatives; the dataset labels become the axis names
 * in the tooltips. Points share one hue — identity lives in the hover tooltip.
 */
@Component({
	standalone:  false,
	selector:    '[cs-scatter-chart]',
	templateUrl: './scatter-chart.component.html'
})
export class CsScatterChartComponent implements OnInit, OnChanges {

	@Input() config: ChartConfig;

	xOffsets = 50;
	yOffsets = 35;
	axisConfig;
	points: ScatterPoint[] = [];

	private isDataReady() {
		return (this.config?.data?.datasets?.length ?? 0) >= 2
			&& this.config.data.datasets[0].data?.length
			&& this.config.data.datasets[1].data?.length;
	}

	ngOnInit() {
		setTimeout(() => {
			if (this.isDataReady()) { this.calculate(); }
		}, 1);
	}

	ngOnChanges(changes: any) {
		if (changes.hasOwnProperty('config') && this.isDataReady()) {
			this.calculate();
		}
	}

	private calculate() {
		if (!this.config.chartWidth || !this.config.chartHeight) { return; }

		const [dsX, dsY, dsSize] = this.config.data.datasets;
		const n  = Math.min(dsX.data.length, dsY.data.length);
		const xs = dsX.data.slice(0, n);
		const ys = dsY.data.slice(0, n);

		const x = niceScale(Math.min(0, ...xs), Math.max(0, ...xs), this.config.ySteps);
		const y = niceScale(Math.min(0, ...ys), Math.max(0, ...ys), this.config.ySteps);

		const plotLeft   = this.config.xPadding + this.xOffsets;
		const plotRight  = this.config.chartWidth - this.config.xPadding - this.xOffsets;
		const plotTop    = this.config.yPadding;
		const plotBottom = this.config.chartHeight - this.config.yPadding - this.yOffsets;
		const xFactor    = (plotRight - plotLeft) / (x.max - x.min);
		const yFactor    = (plotBottom - plotTop) / (y.max - y.min);
		const toX = (v: number) => plotLeft + (v - x.min) * xFactor;
		const toY = (v: number) => plotBottom - (v - y.min) * yFactor;

		this.initAxisConfig(x, y, toX, toY, plotLeft, plotRight, plotTop, plotBottom);

		// Bubble AREA tracks the size value; without a size dataset all points share r=6.
		const sizes   = dsSize?.data?.slice(0, n);
		const maxSize = sizes ? Math.max(...sizes.filter(v => Number.isFinite(v) && v > 0), 1) : 1;

		this.points = xs.map((vx, i) => {
			const vy = ys[i];
			const vs = sizes?.[i];
			const r  = sizes && Number.isFinite(vs) && vs > 0
				? 4 + Math.sqrt(vs / maxSize) * 18
				: 6;
			return {
				cx:    Math.round(toX(vx) * 10) / 10,
				cy:    Math.round(toY(vy) * 10) / 10,
				r:     Math.round(r * 10) / 10,
				fill:  this.config.colors[0],
				label: String(this.config.data.labels?.[i] ?? ''),
				text:  `${dsX.label || 'x'}: ${fmt(vx)}\n${dsY.label || 'y'}: ${fmt(vy)}`
					+ (sizes && Number.isFinite(vs) ? `\n${dsSize.label || 'size'}: ${fmt(vs)}` : '')
			};
		}).filter(p => Number.isFinite(p.cx) && Number.isFinite(p.cy));
	}

	private initAxisConfig(x: Scale, y: Scale, toX: (v: number) => number, toY: (v: number) => number,
	                       left: number, right: number, top: number, bottom: number) {
		this.axisConfig   = { chartWidth: this.config.chartWidth };
		this.axisConfig.s = { x: Math.round(left) + 0.5,  y: Math.round(top) + 0.5 };
		this.axisConfig.e = { x: Math.round(right) + 0.5, y: Math.round(bottom) + 0.5 };
		this.axisConfig.ySteps = [];
		this.axisConfig.xSteps = [];
		for (let v = y.min; v <= y.max + y.step / 2; v += y.step) {
			if (v === y.min && y.min === 0) { continue; }   // axis line already marks the 0 row
			this.axisConfig.ySteps.push({
				text:   this.config.yLabelFormatter(ChartHelper.getValueFormatted(Math.round(v))),
				isZero: y.min < 0 && v === 0,
				y:      Math.round(toY(v)) + 0.5
			});
		}
		for (let v = x.min; v <= x.max + x.step / 2; v += x.step) {
			this.axisConfig.xSteps.push({
				text:     this.config.yLabelFormatter(ChartHelper.getValueFormatted(Math.round(v))),
				fullText: '',
				rotate:   false,
				x:        Math.round(toX(v)) + 0.5
			});
		}
	}
}

interface Scale { min: number; max: number; step: number; }

/** Snap [rawMin, rawMax] (already including 0) to a nice step grid, like the bar chart's axis. */
function niceScale(rawMin: number, rawMax: number, steps: number): Scale {
	const magnitude = Math.max(Math.abs(rawMax), Math.abs(rawMin), 1);
	const step = ChartHelper.getMaxStepValue(magnitude, steps) / steps;
	const min  = Math.floor(rawMin / step) * step;
	const max  = Math.ceil(rawMax / step) * step;
	return { min, max: max === min ? min + step : max, step };
}

function fmt(value: number): string {
	return ChartHelper.getValueLocale(Math.round(value * 100) / 100);
}
